import { defineStore } from 'pinia';
import { useUserStore } from '~/stores/user-store';
import pyscriptApi from '~/utilities/pyscript-api';
import type { Project, Version } from '~/utilities/pyscript-api-models';

const userStore = useUserStore();

interface State {
  project: Project | null;
  versions: Version[];
}

/**
 * Stores the current project being edited or viewed, as well as its versions.
 */
export const useProjectStore = defineStore('project', {
  state: (): State => ({
    project: null,
    versions: [],
  }),

  getters: {
    /**
     * Determine if the current user is the owner of the project.
     */
    isProjectOwner(state) {
      if (userStore.isLoggedIn === false || state.project === null) {
        return false;
      }

      return (
        state.project.user_id === userStore.user?.id ||
        state.project.username === userStore.user?.username
      );
    },
  },

  actions: {
    /**
     * Fetch a project by its slug or id.
     */
    async fetchProjectAndVersions(usernameOrUserId: string, projectSlugOrId: string) {
      const uuidPattern =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89ab][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

      if (uuidPattern.test(projectSlugOrId)) {
        this.project = await pyscriptApi.getProject(projectSlugOrId);
      } else {
        this.project = await pyscriptApi.getProjectBySlug(usernameOrUserId, projectSlugOrId);
      }

      await this.fetchVersions(this.project.id);
    },

    /**
     * Fetch a project's versions.
     */
    async fetchVersions(projectId: string) {
      const versions = await pyscriptApi.listVersions(projectId);
      this.versions = versions.sort((a, b) => {
        if (a.version !== 'latest' && b.version === 'latest') return 1;
        if (a.version === 'latest' && b.version !== 'latest') return -1;
        return Date.parse(b.created_at) - Date.parse(a.created_at);
      });
    },
  },
});
